import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { RegistrationApiService } from '../api/RegistrationApiService';
import ResponsiveFormLayout from '../../../shared/components/layout/ResponsiveFormLayout';
import { useLanguage } from '../../../shared/contexts/LanguageContext';
import { useStepRedirect, skipNextStepRedirect } from '../../../hooks/useStepRedirect';
import { useProgressWithAutoRefresh } from '../../../shared/contexts/ProgressContext';
import { FaUpload, FaCheckCircle, FaImage } from 'react-icons/fa';
import WebcamCapture from '../../../shared/components/ui/WebcamCapture';
import ClinicRoomsUpload, { RoomDocumentState } from '../../../shared/components/ui/ClinicRoomsUpload';
import { normalizeAttachments } from '../../../shared/utils/attachmentUtils';
import { hasPendingFiles, useUnsavedChanges } from "../../../shared/utils/unsavedChanges";
import { ACCEPT_IMAGES, checkUploadFile, uploadProblemKey } from "../../../shared/utils/uploadPolicy";

interface ClinicPhotoScreenProps {
  onNext: () => void;
  onBack: () => void;
  onError: (error: string) => void;
}

interface DocumentState {
  file: File | null;
  preview: string | null;
  uploaded: boolean;
  uploading: boolean;
  existingUrl?: string;
}

const ClinicPhotoScreen: React.FC<ClinicPhotoScreenProps> = ({ onNext, onBack, onError }) => {
  useStepRedirect(); // Protect this page - ensure doctor has completed minimum registration

  const { t } = useLanguage();
  const navigate = useNavigate();
  const { progress } = useProgressWithAutoRefresh();
  const registrationApiRef = useRef(new RegistrationApiService());
  const registrationApi = registrationApiRef.current;

  // Store registration request ID for uploading clinic attachments
  const [requestId, setRequestId] = useState<string | null>(null);

  // Whether no_clinic_in_residence_governorate document is required (from progress endpoint)
  const [requiresNoClinicInResidenceDoc, setRequiresNoClinicInResidenceDoc] = useState(false);
  const [doctorGovernorate, setDoctorGovernorate] = useState<string | null>(null);

  // Document states
  const [signboardOuter, setSignboardOuter] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [signboardInner, setSignboardInner] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [prescription, setPrescription] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [clinicRooms, setClinicRooms] = useState<RoomDocumentState[]>([]);
  const [otherAttachments, setOtherAttachments] = useState<RoomDocumentState[]>([]);

  // Conditional document: required when doctor's governorate differs from clinic's governorate
  const [noClinicInResidence, setNoClinicInResidence] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });
  // Files picked on this device but not uploaded yet: ask before a design switch discards them.
  useUnsavedChanges(hasPendingFiles(signboardOuter, signboardInner, prescription, clinicRooms, otherAttachments, noClinicInResidence));

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false); // New: Track if request is complete (read-only mode)
  const hasLoadedData = useRef(false);

  // Webcam state
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [currentDocType, setCurrentDocType] = useState<'outer' | 'inner' | 'prescription' | 'no_clinic_in_residence' | null>(null);

  // Check if no_clinic_in_residence_governorate document is required from progress
  useEffect(() => {
    if (progress?.active_requests?.length) {
      const registrationRequest = progress.active_requests.find(r => r.request_type === 'registration');
      if (registrationRequest?.documents) {
        const noClinicDoc = registrationRequest.documents.find(
          d => d.document_type === 'no_clinic_in_residence_governorate'
        );
        if (noClinicDoc?.required) {
          console.log('📋 [ClinicPhotoScreen] no_clinic_in_residence_governorate is required');
          setRequiresNoClinicInResidenceDoc(true);
        }
      }
    }
  }, [progress]);

  // Load existing documents when component mounts
  useEffect(() => {
    if (hasLoadedData.current || !progress) return;

    const loadExistingDocuments = async () => {
      setDataLoading(true);
      hasLoadedData.current = true;

      try {
        // Get doctor info to get their syndicate branch governorate (for the conditional document message)
        const doctorInfoResult = await registrationApi.getMyInfo();
        if (doctorInfoResult.isSuccess && doctorInfoResult.data) {
          const doctorInfo = doctorInfoResult.data.doctor_info || doctorInfoResult.data;
          // Doctors whose DMIS membership governorate is missing/unmappable ("Outside Iraq")
          // are served by their residence governorate branch — mirror the backend fallback
          // so the document message names a branch instead of coming out blank.
          const branchGovernorate =
            doctorInfo.syndicate_branch_governorate || doctorInfo.governorate;
          if (branchGovernorate) {
            setDoctorGovernorate(branchGovernorate);
            console.log('👨‍⚕️ [ClinicPhotoScreen] Doctor syndicate branch governorate:', branchGovernorate);
          }
        }

        // Get the request ID from progress
        const activeRegistration = progress?.active_requests?.find(
          (r: any) => r.request_type === 'registration'
        );
        if (activeRegistration) {
          const reqId = activeRegistration.request_id;
          setRequestId(reqId);
          console.log('🆔 [ClinicPhotoScreen] Registration request ID:', reqId);

          // Fetch full details with attachments from detail endpoint
          let rawDocs: any = {};
          const detailResult = await registrationApi.getRegistrationWithAttachments(reqId);
          if (detailResult.isSuccess && detailResult.data) {
            rawDocs = detailResult.data.attachments || detailResult.data.documents || rawDocs;
          }
          const documents = normalizeAttachments(rawDocs);

          console.log('📄 [ClinicPhotoScreen] Loaded documents:', documents);

          // Check if the request has all required attachments (complete)
          const hasOuterSignboard = !!documents.clinic_signboard_outer;
          const hasInnerSignboard = !!documents.clinic_signboard_inner;
          const hasPrescription = !!documents.prescription_image;
          const hasClinicRooms = documents.clinic_rooms && Array.isArray(documents.clinic_rooms) && documents.clinic_rooms.length > 0;
          const isComplete = hasOuterSignboard && hasInnerSignboard && hasPrescription && hasClinicRooms;

          if (isComplete) {
            console.log('✅ [ClinicPhotoScreen] Request is complete - switching to read-only mode');
            setIsReadOnly(true);
          } else {
            console.log('⚠️ [ClinicPhotoScreen] Request is incomplete - allowing edits');
          }

          // Load signboard outer
          if (documents.clinic_signboard_outer) {
            setSignboardOuter({
              file: null,
              preview: documents.clinic_signboard_outer,
              uploaded: true,
              uploading: false,
              existingUrl: documents.clinic_signboard_outer,
            });
          }

          // Load signboard inner
          if (documents.clinic_signboard_inner) {
            setSignboardInner({
              file: null,
              preview: documents.clinic_signboard_inner,
              uploaded: true,
              uploading: false,
              existingUrl: documents.clinic_signboard_inner,
            });
          }

          // Load prescription
          if (documents.prescription_image) {
            setPrescription({
              file: null,
              preview: documents.prescription_image,
              uploaded: true,
              uploading: false,
              existingUrl: documents.prescription_image,
            });
          }

          // Load clinic rooms (array)
          if (documents.clinic_rooms && Array.isArray(documents.clinic_rooms)) {
            const roomStates = documents.clinic_rooms.map((url: string) => ({
              file: null,
              preview: url,
              uploaded: true,
              uploading: false,
              existingUrl: url,
            }));
            setClinicRooms(roomStates);
          } else {
            // Initialize with one empty room slot if no existing rooms
            setClinicRooms([{
              file: null,
              preview: null,
              uploaded: false,
              uploading: false,
            }]);
          }

          // Load other attachment slots — prefer per-slot types, fall back to legacy array.
          const legacyOther = documents.other_attachments
            ? (Array.isArray(documents.other_attachments) ? documents.other_attachments : [documents.other_attachments])
            : [];
          const slot1Url = documents.clinic_other_attachment_1 || legacyOther[0];
          const slot2Url = documents.clinic_other_attachment_2 || legacyOther[1];
          const otherSlots = [slot1Url, slot2Url]
            .filter(Boolean)
            .map((url: string) => ({
              file: null,
              preview: url,
              uploaded: true,
              uploading: false,
              existingUrl: url,
            }));
          if (otherSlots.length > 0) {
            setOtherAttachments(otherSlots);
          }

          // Load no_clinic_in_residence_governorate document if it exists
          if (documents.no_clinic_in_residence_governorate) {
            setNoClinicInResidence({
              file: null,
              preview: documents.no_clinic_in_residence_governorate,
              uploaded: true,
              uploading: false,
              existingUrl: documents.no_clinic_in_residence_governorate,
            });
          }
        }
      } catch (error) {
        console.error('Failed to load clinic documents:', error);
      } finally {
        setDataLoading(false);
      }
    };

    loadExistingDocuments();
  }, [progress]);

  const handleFileSelect = (
    type: 'outer' | 'inner' | 'prescription' | 'no_clinic_in_residence',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Same type/size rules as the server (shared/utils/uploadPolicy).
    const problem = checkUploadFile(file, { allowPdf: false });
    if (problem) {
      onError(t(uploadProblemKey(problem, false)));
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    if (type === 'outer') {
      setSignboardOuter({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (type === 'inner') {
      setSignboardInner({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (type === 'prescription') {
      setPrescription({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (type === 'no_clinic_in_residence') {
      setNoClinicInResidence({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    }
  };

  // Handle webcam capture
  const handleWebcamCapture = (file: File) => {
    if (!currentDocType) return;

    const previewUrl = URL.createObjectURL(file);

    if (currentDocType === 'outer') {
      setSignboardOuter({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (currentDocType === 'inner') {
      setSignboardInner({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (currentDocType === 'prescription') {
      setPrescription({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    } else if (currentDocType === 'no_clinic_in_residence') {
      setNoClinicInResidence({
        file,
        preview: previewUrl,
        uploaded: false,
        uploading: false,
      });
    }
  };

  const openWebcam = (type: 'outer' | 'inner' | 'prescription' | 'no_clinic_in_residence') => {
    setCurrentDocType(type);
    setWebcamOpen(true);
  };

  const uploadDocument = async (file: File, documentType: string): Promise<boolean> => {
    if (!requestId) {
      console.error('❌ No request ID available for upload');
      onError(t('registration_request_not_found'));
      return false;
    }

    try {
      const result = await registrationApi.uploadClinicAttachment(file, documentType, requestId);

      if (result.isSuccess) {
        console.log(`✅ Uploaded ${documentType} successfully`);
        return true;
      } else {
        console.error(`❌ Failed to upload ${documentType}:`, result.error);
        onError(result.error || t('failed_to_upload_document_type').replace('{type}', documentType));
        return false;
      }
    } catch (error) {
      console.error(`❌ Error uploading ${documentType}:`, error);
      onError(error instanceof Error ? error.message : t('upload_failed'));
      return false;
    }
  };

  const handleSubmit = async () => {
    // Validate required documents
    if (!signboardOuter.file && !signboardOuter.existingUrl) {
      onError(t('please_upload_clinic_signboard_outer'));
      return;
    }

    if (!signboardInner.file && !signboardInner.existingUrl) {
      onError(t('please_upload_clinic_signboard_inner'));
      return;
    }

    if (!prescription.file && !prescription.existingUrl) {
      onError(t('please_upload_prescription_image'));
      return;
    }

    // Validate at least one clinic room photo
    if (clinicRooms.length === 0) {
      onError(t('please_upload_at_least_one_room_photo') || 'Please upload at least one clinic room photo');
      return;
    }

    // Check if at least one room has a file or existing URL
    const hasValidRoom = clinicRooms.some(room => room.file || room.existingUrl);
    if (!hasValidRoom) {
      onError(t('please_upload_at_least_one_room_photo') || 'Please upload at least one clinic room photo');
      return;
    }

    // Validate no_clinic_in_residence_governorate if required
    if (requiresNoClinicInResidenceDoc && !noClinicInResidence.file && !noClinicInResidence.existingUrl) {
      const errorMsg = (t('please_upload_no_clinic_in_residence') || 'Please upload confirmation from the syndicate branch in {governorate}')
        .replace('{governorate}', doctorGovernorate || '');
      onError(errorMsg);
      return;
    }

    setLoading(true);

    try {
      let allSuccess = true;

      // Upload outer signboard sequentially
      if (signboardOuter.file) {
        setSignboardOuter(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(signboardOuter.file, 'clinic_signboard_outer');
        setSignboardOuter(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) {
          allSuccess = false;
        }
      }

      // Upload inner signboard sequentially
      if (signboardInner.file) {
        setSignboardInner(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(signboardInner.file, 'clinic_signboard_inner');
        setSignboardInner(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) {
          allSuccess = false;
        }
      }

      // Upload prescription sequentially
      if (prescription.file) {
        setPrescription(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(prescription.file, 'prescription_image');
        setPrescription(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) {
          allSuccess = false;
        }
      }

      // Upload clinic room photos sequentially
      for (let index = 0; index < clinicRooms.length; index++) {
        const room = clinicRooms[index];
        if (room.file) {
          setClinicRooms(prev => {
            const newRooms = [...prev];
            newRooms[index] = { ...newRooms[index], uploading: true };
            return newRooms;
          });

          const success = await uploadDocument(room.file, 'clinic_rooms');

          setClinicRooms(prev => {
            const newRooms = [...prev];
            newRooms[index] = { ...newRooms[index], uploading: false, uploaded: success };
            return newRooms;
          });

          if (!success) {
            allSuccess = false;
          }
        }
      }

      // Upload no_clinic_in_residence_governorate if required and has new file
      if (requiresNoClinicInResidenceDoc && noClinicInResidence.file) {
        setNoClinicInResidence(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(noClinicInResidence.file, 'no_clinic_in_residence_governorate');
        setNoClinicInResidence(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) {
          allSuccess = false;
        }
      }

      // Upload other_attachments
      for (let index = 0; index < otherAttachments.length; index++) {
        const att = otherAttachments[index];
        if (att.file) {
          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: true };
            return updated;
          });

          const slotType = index === 0 ? 'clinic_other_attachment_1' : 'clinic_other_attachment_2';
          const success = await uploadDocument(att.file, slotType);

          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: false, uploaded: success };
            return updated;
          });

          if (!success) {
            allSuccess = false;
          }
        }
      }

      if (allSuccess) {
        console.log('✅ All clinic photos uploaded successfully');
        console.log('🔄 Navigating to review...');
        skipNextStepRedirect();
        navigate('/review');
      } else {
        console.error('❌ Some document(s) failed to upload');
        onError(t('some_documents_failed_to_upload'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      onError(error instanceof Error ? error.message : t('failed_to_upload_documents'));
    } finally {
      setLoading(false);
    }
  };

  const DocumentUploadCard = ({
    title,
    description,
    state,
    onFileSelect,
    onTakePhoto,
    required = false,
    inputId,
  }: {
    title: string;
    description: string;
    state: DocumentState;
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onTakePhoto: () => void;
    required?: boolean;
    inputId: string;
  }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {title}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>

      {state.preview ? (
        <div className="relative">
          <img
            src={state.preview}
            alt={title}
            className="w-full h-48 object-cover rounded-lg"
          />
          {state.uploaded && !state.uploading && (
            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
              <FaCheckCircle size={20} />
            </div>
          )}
          {state.uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
            </div>
          )}
          {!isReadOnly && (
            <label className={`mt-3 w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
              state.uploading || loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer'
            }`}>
              <FaUpload />
              <span>{t('change_photo')}</span>
              <input
                type="file"
                accept={ACCEPT_IMAGES}
                onChange={onFileSelect}
                className="hidden"
                disabled={state.uploading || loading}
              />
            </label>
          )}
        </div>
      ) : isReadOnly ? (
        <div className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg border-gray-200 bg-gray-50">
          <FaImage className="w-12 h-12 text-gray-400 mb-3" />
          <p className="text-sm text-gray-600">
            <span className="font-semibold">{t('no_photo_uploaded')}</span>
          </p>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-2">
            {/* Take Photo Button (Camera) */}
            <button
              onClick={onTakePhoto}
              disabled={state.uploading || loading}
              type="button"
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-4xl mb-2">📷</div>
              <span className="text-sm font-medium text-gray-700">{t('take_photo') || 'Take Photo'}</span>
            </button>

            {/* Or Divider */}
            <span className="text-sm text-gray-500 font-medium">{t('or') || 'Or'}</span>

            {/* Choose File Button */}
            <label
              htmlFor={`${inputId}-file`}
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <FaUpload className="text-3xl text-gray-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('choose_from_files') || 'Choose from Files'}</span>
              <input
                id={`${inputId}-file`}
                type="file"
                className="hidden"
                accept={ACCEPT_IMAGES}
                onChange={onFileSelect}
                disabled={state.uploading || loading}
              />
            </label>
          </div>

          <p className="text-xs text-center text-gray-400">{t('png_jpg_up_to_10mb')}</p>
        </div>
      )}
    </div>
  );

  const actionButtons = (
    <div className="flex gap-3 w-full">
      <button
        onClick={onBack}
        disabled={loading || dataLoading}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t('back') || 'Back'}
      </button>
      <button
        onClick={isReadOnly ? onNext : handleSubmit}
        disabled={loading || dataLoading}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading || dataLoading
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
        }`}
        style={{
          backgroundColor: loading || dataLoading ? '#B0B0B0' : '#2E5D7D',
        }}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>{t('uploading')}</span>
          </>
        ) : (
          <span>{isReadOnly ? t('continue') : t('upload_and_continue')}</span>
        )}
      </button>
    </div>
  );

  if (dataLoading) {
    return (
      <ResponsiveFormLayout
        showLanguageToggle={true}
        icon="/ima_logo.png"
        iconSize="w-32 h-32"
        title={t('clinic_photos')}
        subtitle={t('loading_existing_documents')}
        maxHeight="90vh"
      >
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2E5D7D] border-t-transparent"></div>
          <span className="ml-3 text-gray-600">{t('loading')}</span>
        </div>
      </ResponsiveFormLayout>
    );
  }

  return (
    <ResponsiveFormLayout
      onBack={onBack}
      showBackButton={true}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      iconSize="w-32 h-32"
      title={t('clinic_photos')}
      subtitle={t('upload_photos_clinic_signboards_rooms')}
      actions={actionButtons}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Read-only mode banner */}
        {isReadOnly && (
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <p className="text-sm text-green-800">
              <strong>{t('registration_complete_banner_title')}</strong> {t('registration_complete_banner_message')}
            </p>
          </div>
        )}

        {/* Signboard Outer */}
        <DocumentUploadCard
          title={t('clinic_signboard_outer_title')}
          description={t('clinic_signboard_outer_description')}
          state={signboardOuter}
          onFileSelect={(e) => handleFileSelect('outer', e)}
          onTakePhoto={() => openWebcam('outer')}
          required
          inputId="signboard-outer"
        />

        {/* Signboard Inner */}
        <DocumentUploadCard
          title={t('clinic_signboard_inner_title')}
          description={t('clinic_signboard_inner_description')}
          state={signboardInner}
          onFileSelect={(e) => handleFileSelect('inner', e)}
          onTakePhoto={() => openWebcam('inner')}
          required
          inputId="signboard-inner"
        />

        {/* Prescription */}
        <DocumentUploadCard
          title={t('prescription_image_title')}
          description={t('prescription_image_description')}
          state={prescription}
          onFileSelect={(e) => handleFileSelect('prescription', e)}
          onTakePhoto={() => openWebcam('prescription')}
          required
          inputId="prescription"
        />

        {/* Clinic Rooms */}
        <ClinicRoomsUpload
          rooms={clinicRooms}
          onRoomsChange={setClinicRooms}
          onError={onError}
          isReadOnly={isReadOnly}
          loading={loading}
        />

        {/* No Clinic in Residence Governorate - only shown when required */}
        {requiresNoClinicInResidenceDoc && (
          <DocumentUploadCard
            title={t('no_clinic_in_residence_title') || 'Confirmation of No Clinic in Residence Governorate'}
            description={
              (t('no_clinic_in_residence_description') || 'Upload confirmation from the syndicate branch in {governorate}')
                .replace('{governorate}', doctorGovernorate || '')
            }
            state={noClinicInResidence}
            onFileSelect={(e) => handleFileSelect('no_clinic_in_residence', e)}
            onTakePhoto={() => openWebcam('no_clinic_in_residence')}
            required
            inputId="no-clinic-in-residence"
          />
        )}

        {/* Other Attachments (optional, max 2) */}
        <ClinicRoomsUpload
          rooms={otherAttachments}
          onRoomsChange={setOtherAttachments}
          onError={onError}
          isReadOnly={isReadOnly}
          loading={loading}
          title={t('other_attachments') || 'Other Attachments'}
          required={false}
          maxCount={2}
          itemLabel={t('attachment') || 'Attachment'}
        />

        {/* Info box */}
        {!isReadOnly && (
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>{t('note_label')}</strong> {t('clinic_photos_note_message')}
            </p>
          </div>
        )}
      </div>

      {/* Webcam Modal */}
      {webcamOpen && (
        <WebcamCapture
          onCapture={handleWebcamCapture}
          onClose={() => setWebcamOpen(false)}
        />
      )}
    </ResponsiveFormLayout>
  );
};

export default ClinicPhotoScreen;
